import uniq from 'lodash-es/uniq';
import { defineMessage } from 'react-intl';

import { variations } from './data/share';

export const documentsTypes = [
    {
        type: 'article',
        isDefault: true,
    },
    {
        type: 'article_legacy',
        path: 'article',
    },
    {
        type: 'video',
    },
    {
        type: 'micromag',
    },
    {
        type: 'podcast',
        path: 'balado',
    },
    {
        type: 'contest',
        path: 'concours',
    },
    {
        type: 'interactive',
        path: 'interactif',
    },
    {
        type: 'newsletter',
        path: 'infolettre',
    },
    {
        type: 'survey',
        path: 'sondage',
    },
];

export const collectionsTypes = [
    {
        type: 'collection',
        isDefault: true,
    },
    {
        type: 'serie',
    },
    {
        type: 'serie_podcast',
        path: 'serie',
    },
];

export const typesSections = [
    {
        id: 'all',
        label: 'Tout',
        title: 'Tous les contenus',
        path: 'tout',
        subSection: true,
        sponso: true,
    },
    {
        id: 'popular',
        label: 'Populaire',
        title: 'Les plus populaires',
        path: 'populaire',
        subSection: true,
    },
    {
        id: 'articles',
        label: 'Articles',
        title: 'Tous les articles',
        path: 'articles',
        query: {
            type: ['article', 'article_legacy'],
        },
        subSection: true,
        sponso: true,
    },
    {
        id: 'videos',
        label: 'Vidéos',
        title: 'Vidéos',
        indexTitle: 'Toutes les vidéos',
        path: 'videos',
        query: {
            type: 'video',
        },
        all: true,
        subSection: true,
        sponso: true,
    },
    {
        id: 'micromags',
        label: 'Micromag',
        title: 'Micromag',
        indexTitle: 'Tous les micromags',
        path: 'micromag',
        all: true,
        brands: ['urbania', 'france'],
        query: {
            type: 'micromag',
        },
    },
    {
        id: 'podcasts',
        label: 'Balados',
        title: 'Balados',
        indexTitle: 'Tous les balados',
        path: 'balados',
        query: {
            type: 'podcast',
        },
        all: true,
        sponso: true,
    },
    {
        id: 'series',
        label: 'Séries',
        title: 'Toutes les séries',
        path: 'series',
        itemsType: 'collection',
        query: {
            type: 'serie',
        },
        sponso: true,
    },
    {
        id: 'collections',
        label: 'Collections',
        title: 'Toutes les collections',
        path: 'collections',
        itemsType: 'collection',
        sponso: true,
        query: {
            exclude_type: ['serie', 'serie_podcast'],
        },
    },
    {
        id: 'newsletters',
        label: defineMessage({
            defaultMessage: 'Infolettres',
            description: 'Type section label',
        }),
        title: defineMessage({
            defaultMessage: 'Toutes les infolettres',
            description: 'Type section title',
        }),
        path: 'infolettres',
        query: {
            type: 'newsletter',
        },
    },
];

export function documentTypeFromPath(path) {
    const defaultType = documentsTypes.find(({ isDefault = false }) => isDefault === true);
    const { type } =
        documentsTypes.find(({ type, path: typePath }) => typePath === path || type === path) ||
        defaultType;
    return type;
}

export function pathFromDocumentType(typeId) {
    const defaultType = documentsTypes.find(({ isDefault = false }) => isDefault === true);
    const { type, path } =
        documentsTypes.find(({ type: docType }) => docType === typeId) || defaultType;
    return path || type;
}

export function collectionTypeFromPath(path) {
    const defaultType = collectionsTypes.find(({ isDefault = false }) => isDefault === true);
    const { type } =
        collectionsTypes.find(({ type, path: typePath }) => typePath === path || type === path) ||
        defaultType;
    return type;
}

export function pathFromCollectionType(typeId) {
    const defaultType = collectionsTypes.find(({ isDefault = false }) => isDefault === true);
    const { type, path } =
        collectionsTypes.find(({ type: docType }) => docType === typeId) || defaultType;
    return path || type;
}

export function createRoutes(site) {
    const { brands = null, regions = null, locationTypes = null } = site;

    const brandsWithSlug = brands.filter(({ slug = null }) => slug !== null);
    const brandPattern =
        brandsWithSlug.length > 0 ? brandsWithSlug.map(({ slug }) => slug).join('|') : null;

    const sections = brands.reduce(
        (allSections, { sections: brandSections = [] }) => [...allSections, ...brandSections],
        [],
    );
    const sectionsPattern = [...sections.map(({ slug }) => slug)].join('|');
    const subTypesPattern = typesSections
        .filter(({ subSection = true }) => subSection)
        .map(({ path }) => path)
        .join('|');
    const sectionsTypesPattern = typesSections
        .filter(({ id }) => id !== 'all')
        .map(({ path }) => path)
        .join('|');

    const variationsSlugs = variations.map(({ id }) => id);

    const documentTypePattern = uniq(documentsTypes.map(({ type, path }) => path || type)).join(
        '|',
    );

    const collectionTypePattern = uniq(collectionsTypes.map(({ type, path }) => path || type)).join(
        '|',
    );

    const brandPath = brandPattern !== null ? `/:brand(${brandPattern})?` : '';

    return {
        'brand.wildcard': `/:brand(${brandPattern || 'no-brand'})/:path(.*)?`,

        home: `${brandPath || '/'}`,
        section: `${brandPath}/:section(${sectionsPattern})/:type(${subTypesPattern})?`,
        'section.type': `${brandPath}/:section(${sectionsPattern})/:type(${subTypesPattern})`,
        favicon: `${brandPath}/favicon.png`,
        shareImage: `${brandPath}/share.jpg`,

        menu: '/menu',
        search: '/recherche',

        'subscription.wildcard': `${brandPath}/:path(abonnement|abonner|abonnement/ami)`,
        subscription: `${brandPath}/abonnement`,
        'subscription.friend': `${brandPath}/abonnement/ami`,
        subscribe: `${brandPath}/abonner`,

        ...typesSections.reduce(
            (acc, { id, path, all = false }) =>
                all
                    ? {
                          ...acc,
                          [id]: `${brandPath}/${path}`,
                          [`${id}.all`]: `${brandPath}/${path}/tout`,
                      }
                    : {
                          ...acc,
                          [id]: `${brandPath}/${path}`,
                      },
            {},
        ),

        sponso: `${brandPath}/sponso/:type(${sectionsTypesPattern})?`,

        document: `/:type(${documentTypePattern})/:slug/:path([^/.]+)?`,
        'document.share': `/:type(${documentTypePattern})/:slug/partage`,
        'document.share.wildcard': `/:type(${documentTypePattern})/:slug/partage/:path(.*)?`,
        'document.share_card': `/:type(${documentTypePattern})/:slug/partage/:variation(${variationsSlugs.join('|')})`,
        'document.share_image': `/:type(${documentTypePattern})/:slug/partage/:variation(${variationsSlugs.join('|')}).jpg`,
        ...documentsTypes.reduce(
            (acc, { type, path: typePath }) => ({
                ...acc,
                [type]: `/${typePath || type}/:slug/:path([^/.]+)?`,
            }),
            {},
        ),
        'interactive.share_image': '/interactif/:slug/:path/share.jpg',
        preview: '/preview/:token([^/]+)/:path([^/.]+)?',

        collection: `/:type(${collectionTypePattern})/:slug/:path([^/.]+)?`,

        authors: '/auteurs',
        author: `/auteurs/:author/:type(${subTypesPattern})?`,

        categories: '/categories',
        category: `/categorie/:slug/:type(${subTypesPattern})?`,

        topics: '/sujets',
        topic: `/sujet/:slug/:type(${subTypesPattern})?`,

        formats: '/formats',
        format: `/format/:slug/:type(${subTypesPattern})?`,

        map: '/carte',
        'map.region':
            regions !== null && regions.length > 0
                ? `/carte/:region(${regions.map(({ slug }) => slug).join('|')})`
                : '/carte/:region',
        'map.location': '/carte/lieux/:location',
        'map.locationType':
            locationTypes !== null && locationTypes.length > 0
                ? `/carte/:locationType(${locationTypes.map(({ slug }) => slug).join('|')})`
                : '/carte/:locationType',
        'map.document': '/carte/contenu/:document',
        'map.wildcard': '/carte/:slug?/:subslug?',

        ai: '/ai/:slug',

        page: '/:slug',

        pro: '/pro/:section?',

        login: '/connexion',
        register: '/inscription',
        logout: '/deconnexion',
        'auth.wildcard': '/:slug(connexion|inscription|deconnexion)',

        account: '/compte',
        'account.profile': '/compte/profil',
        'account.subscriptions': '/compte/abonnements',
        'account.timeline': '/compte/historique',
        'account.stats': '/compte/statistiques',
        'account.promotions': '/compte/avantages',
        'account.wildcard': '/compte/:section?',
    };
}
